import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lock, Loader2, LogOut, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useToolsAuth } from "@/context/ToolsAuthContext";
import { toolsApi, formatApiError, goToCheckout } from "@/lib/toolsApi";
import { PRODUCTS } from "@/components/tools/Workbook";
import { PricingCards } from "@/components/tools/PricingCards";

const tools = Object.values(PRODUCTS);

export const ToolsPaywall = () => {
  const { user, login, register, logout } = useToolsAuth();
  const [authMode, setAuthMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", first_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);

  const startCheckout = async (lookupKey) => {
    setBusy(true);
    try {
      const { data } = await toolsApi.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      goToCheckout(data.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const choosePlan = (lookupKey) => {
    if (user) {
      startCheckout(lookupKey);
    } else {
      setPendingPlan(lookupKey);
      setError("");
      const el = document.getElementById("tools-auth");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (authMode === "register") {
        await register(form.email, form.password, form.first_name);
      } else {
        await login(form.email, form.password);
      }
      if (pendingPlan) {
        await startCheckout(pendingPlan);
        return;
      }
      toast.success("You're signed in.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="tools-paywall" className="bg-cream min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-5">The Toolkit</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-wine leading-[1.05]">
            Your Tools, ready when you are.
          </h1>
          <p className="mt-6 text-base md:text-lg text-charcoal/70 font-light leading-relaxed max-w-2xl">
            Five interactive, auto-saving worksheets built on the RESET Method™ — designed to help you plan
            your week, lighten the mental load, and protect what matters. Unlock the full toolkit with a
            one-time purchase or a membership. Prefer paper? The 5-Minute Reset™ is free as a PDF.
          </p>
          <button
            onClick={() => {
              const el = document.getElementById("pricing");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="paywall-view-plans"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
          >
            View plans <ArrowRight size={15} />
          </button>
        </motion.div>

        {/* Locked preview — every tool is locked until purchase */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tools-locked-preview">
          {tools.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`locked-tool-${t.id}`}
                className="relative rounded-2xl border border-gold/40 bg-white/50 p-6 overflow-hidden"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blush/60 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-wine" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-wine leading-tight">{t.name}</h3>
                  </div>
                </div>
                <p className="mt-4 text-sm text-charcoal/70 font-light">{t.tagline}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-terracotta/80">
                  <Lock size={13} /> Locked
                </div>
              </motion.div>
            );
          })}
        </div>
        <p className="mt-6 text-sm text-charcoal/60" data-testid="free-pdf-note">
          Looking for something free? Get the{" "}
          <Link to="/downloads" className="text-terracotta underline underline-offset-2 hover:text-wine">
            5-Minute Reset™ as a free PDF
          </Link>
          .
        </p>

        {/* Pricing */}
        <div className="mt-24" id="pricing">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">Choose your access</p>
            <h2 className="font-serif font-light text-3xl md:text-4xl text-wine">One toolkit, two ways in.</h2>
          </div>

          <div className="mt-14">
            <PricingCards onChoose={choosePlan} busy={busy} />
          </div>
        </div>

        {/* Auth */}
        {!user && (
          <div id="tools-auth" className="mt-24 max-w-md mx-auto" data-testid="tools-auth">
            <div className="text-center mb-8">
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-3">
                {pendingPlan ? "One step to checkout" : "Purchase or sign in"}
              </p>
              <h2 className="font-serif font-light text-3xl text-wine">
                {authMode === "register" ? "Create your account" : "Welcome back"}
              </h2>
              <p className="mt-3 text-sm text-charcoal/60">
                {authMode === "register"
                  ? "You'll use this to unlock and access your tools on any device."
                  : "Sign in to open your toolkit."}
              </p>
            </div>
            <form onSubmit={submitAuth} className="space-y-6 border border-gold/40 bg-white/50 p-8" data-testid="tools-auth-form">
              {authMode === "register" && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">First name (optional)</label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="tools-firstname-input"
                    className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                  />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="tools-email-input"
                  className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Password</label>
                <input
                  required
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="tools-password-input"
                  className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                />
                {authMode === "register" && <p className="mt-2 text-xs text-charcoal/50">At least 8 characters.</p>}
              </div>
              {error && <p className="text-sm text-destructive" data-testid="tools-auth-error">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                data-testid="tools-auth-submit"
                className="w-full rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300 disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                {authMode === "register" ? "Create account" : "Sign in"}
              </button>
              <p className="text-center text-sm text-charcoal/60">
                {authMode === "register" ? "Already have an account?" : "Need an account?"}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode(authMode === "register" ? "login" : "register");
                    setError("");
                  }}
                  data-testid="tools-auth-toggle"
                  className="text-terracotta underline underline-offset-2 hover:text-wine"
                >
                  {authMode === "register" ? "Sign in" : "Create one"}
                </button>
              </p>
            </form>
          </div>
        )}

        {user && (
          <div className="mt-20 max-w-md mx-auto text-center" data-testid="tools-signed-in">
            <p className="text-sm text-charcoal/70" data-testid="tools-signed-in-email">
              Signed in as <span className="font-medium">{user.email}</span>. Choose a plan above to unlock your tools.
            </p>
            <button
              onClick={logout}
              data-testid="tools-signed-in-logout"
              className="mt-4 inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-wine transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        )}
      </div>
    </main>
  );
};
